"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface AnimatedMalekLogoProps {
  width?: number;
  height?: number;
  className?: string;
  strokeWidth?: number;
  autoPlay?: boolean;
  duration?: number;
  delay?: number;
}

export default function AnimatedMalekLogo({
  width = 255,
  height = 130,
  className = '',
  strokeWidth = 8,
  autoPlay = true,
  duration = 1.0,
  delay = 1,
}: AnimatedMalekLogoProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    // Get the total length of the path
    const pathLength = path.getTotalLength();

    // Set up the stroke-dasharray and stroke-dashoffset
    path.style.strokeDasharray = `${pathLength}`;
    path.style.strokeDashoffset = `${pathLength}`;

    if (autoPlay) {
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
          delay: delay,
        },
      );
    }

    // Hover animation function
    const animateDraw = () => {
      gsap.fromTo(
        path,
        {
          strokeDashoffset: pathLength,
        },
        {
          strokeDashoffset: 0,
          duration: duration,
          delay: delay,
        },
      );
    };

    // Add hover listeners
    const svg = svgRef.current;
    if (svg && !autoPlay) {
      svg.addEventListener('mouseenter', animateDraw);
      return () => {
        svg.removeEventListener('mouseenter', animateDraw);
      };
    }
  }, [autoPlay, duration, delay]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox="0 0 554 138"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Animated Malek Signature"
      role="img"
    >
      <path
        ref={pathRef}
        d="M13.5256 130.86C13.5256 130.86 10.0343 126.08 10.0343 121.004C10.0344 107.071 51.4267 75.4392 68.6636 63.4728C99.9014 41.7865 172.972 -3.52891 181.814 9.65041C190.656 22.8297 109.927 97.07 118.869 103.625C125.744 108.665 243.854 8.18783 239.596 29.6511C233.978 57.9671 167.549 123.862 196.442 114.437C223.59 105.581 245.542 97.4384 255.339 92.3338C284.181 77.3062 291.257 87.6166 289.303 86.6673C281.119 82.6917 270.501 84.9884 267.377 86.6673C263.621 88.6849 259.864 93.0767 262.495 96.4308C266.728 101.826 276.768 97.2531 279.196 96.4308C286.619 93.9158 297.378 87.7865 299.478 89.5709C300.82 90.7112 292.2 96.0401 298.209 99.514C304.217 102.988 323.95 97.8125 338.804 88.1557C353.659 78.4989 394.9 39.6483 393.018 39.6483C391.135 39.6483 374.644 50.969 364.755 60.4599C352.607 72.1182 338.727 87.285 337.89 97.1857C337.529 101.449 343.072 102.225 347.283 101.826C351.494 101.428 367.054 98.8891 379.143 94.8892C387.174 92.2318 402.746 88.3638 399.348 85.4602C394.783 81.5598 385.162 82.293 380.546 86.134C376.765 89.2812 377.918 95.7032 382.972 97.5227C385.035 98.2651 393.018 100.69 404.469 98.8031C425.607 95.3202 437.324 93.2725 456.48 84.0979C482.712 71.5344 517.492 35.9575 515.054 37.3449C498.233 46.914 470.375 74.2063 470.375 74.2063C470.375 74.2063 450.863 92.5577 453.512 98.0838C454.955 101.094 461.054 96.4308 473.741 89.6639C478.759 86.9872 481.846 88.6181 483.204 87.6884C484.561 86.7588 482.582 84.4692 477.715 86.134C473.794 87.4753 469.23 89.6046 468.639 92.2469C468.048 94.8892 468.972 97.0647 474.63 98.0838C480.289 99.103 496.815 97.6862 503.889 96.4308C510.963 95.1754 518.663 93.2117 528.036 90.505C533.979 88.7887 543.404 86.6673 543.404 86.6673"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
